/**
 * bimShadeParts — the parametric shade geometry, ported from the house-ifc
 * Python device modules (card t_f81aa9a1).
 *
 * The live device layer renders a shade from these part boxes instead of the
 * baked twin the hub serves, so the port has to BE the same geometry:
 * every number below is the Python module's own constant, in metres:
 *
 *   devices/venetian-blind/venetian_blind.py   blind_solids()   (SLAT_* / HEAD_BOX_* / GUIDE_* / BOTTOM_* / *_GAP)
 *   devices/zip-roller/zip_roller.py           shade_solids()   (CASSETTE_* / RAIL_* / FABRIC_* / HEM_*)
 *   devices/roof-shutter/roof_shutter.py       shutter_solids() (same shape, slim skylight proportions)
 *
 * Local frame of every product (the modules' own contract, mm → m):
 *   origin = TOP of the head box / cassette, centred on the width;
 *   +X = along the width, +Y = OUT of the mounting plane, +Z = up;
 *   the product occupies z ∈ [-drop, 0].
 *
 * A part is a box in that frame: centre + full size, plus an optional rotation
 * about X (the slats). `bimShadeParts.test.ts` pins every case against output
 * generated by the Python modules themselves.
 */

import type { ShadeModelType, Vec3 } from './bimShadeSpec';

/** Which shared material a part uses (the modules' style keys). */
export type ShadePartMaterial = 'body' | 'slat' | 'railBody' | 'fabric';

export interface ShadePartBox {
  /** Stable slot key: 'head' | 'guide-l' | 'guide-r' | 'slat-<i>' | 'rail' |
   *  'cassette' | 'rail-l' | 'rail-r' | 'fabric' | 'hem'. */
  key: string;
  material: ShadePartMaterial;
  /** Box centre, metres, in the shade's local frame. */
  center: Vec3;
  /** Full box size, metres: [X = width direction, Y = out, Z = up]. */
  size: Vec3;
  /** Slats only: rotation about local X, degrees (the slat angle). */
  tiltXDeg?: number;
}

const MM = 0.001;

// ─── venetian blind (devices/venetian-blind/venetian_blind.py) ──────────────

export const VENETIAN = {
  slatPitch: 78 * MM,
  slatDepth: 75 * MM,
  slatThick: 1.2 * MM,
  headBoxH: 92 * MM,
  headBoxD: 96 * MM,
  guideW: 24 * MM,
  guideD: 26 * MM,
  guideInset: 10 * MM,
  bottomRailH: 24 * MM,
  bottomRailD: 38 * MM,
  slatEndGap: 26 * MM,
  topGap: 8 * MM,
  bottomGap: 30 * MM,
  mountStandoff: 60 * MM,
  /** The slat's own centre plane, out of the facade (module: `_slat(model, 50.0, …)`). */
  slatY: 50 * MM,
} as const;

// ─── zip roller / roof shutter (same shape, different proportions) ──────────

export interface RollerConstants {
  cassetteH: number;
  cassetteD: number;
  railW: number;
  railD: number;
  railY: number;
  fabricThick: number;
  fabricTuck: number;
  fabricY: number;
  hemH: number;
  hemD: number;
  mountStandoff: number;
}

export const ZIP: RollerConstants = {
  cassetteH: 105 * MM,
  cassetteD: 96 * MM,
  railW: 28 * MM,
  railD: 34 * MM,
  railY: 6 * MM,
  fabricThick: 4 * MM,
  fabricTuck: 18 * MM,
  fabricY: 14 * MM,
  hemH: 26 * MM,
  hemD: 38 * MM,
  mountStandoff: 60 * MM,
};

export const ROOF_SHUTTER: RollerConstants = {
  cassetteH: 92 * MM,
  cassetteD: 84 * MM,
  railW: 26 * MM,
  railD: 32 * MM,
  railY: 6 * MM,
  fabricThick: 4 * MM,
  fabricTuck: 18 * MM,
  fabricY: 14 * MM,
  hemH: 24 * MM,
  hemD: 34 * MM,
  mountStandoff: 60 * MM,
};

export function rollerConstants(type: ShadeModelType): RollerConstants {
  return type === 'roof-shutter' ? ROOF_SHUTTER : ZIP;
}

/** The modules' `_box(x0, y0, z0, dx, dy, dz)`: dz < 0 means "hangs downward". */
function boxAt(key: string, material: ShadePartMaterial, x0: number, y0: number, z0: number, dx: number, dy: number, dz: number, tiltXDeg?: number): ShadePartBox {
  const zLow = dz < 0 ? z0 + dz : z0;
  const zSize = Math.abs(dz);
  return {
    key,
    material,
    center: [x0 + dx / 2, y0 + dy / 2, zLow + zSize / 2],
    size: [dx, dy, zSize],
    ...(tiltXDeg === undefined ? {} : { tiltXDeg }),
  };
}

/** The modules' `slat_count(drop_mm)`: how many slats the stack holds. */
export function venetianSlatCount(dropM: number): number {
  const usable = dropM - VENETIAN.headBoxH - VENETIAN.topGap - VENETIAN.bottomRailH - VENETIAN.bottomGap;
  return Math.max(1, Math.floor(usable / VENETIAN.slatPitch) + 1);
}

export interface StyledShadeParams {
  widthM: number;
  dropM: number;
  /** Jalousie only: the slat angle (open -20 … closed -75). */
  tiltDeg?: number;
  cassetteHidden?: boolean;
}

/**
 * `blind_solids()` — head box, two side guides, the slat stack, bottom rail.
 * A hidden cassette tucks the head box back by its own depth + the mounting
 * standoff, so its front face lands on the window's outer face; the slats,
 * guides and rail NEVER move with it.
 */
export function venetianBlindParts(params: StyledShadeParams): ShadePartBox[] {
  const { widthM: w, dropM: d } = params;
  const tiltDeg = params.tiltDeg ?? -20;
  const hidden = params.cassetteHidden === true;
  const C = VENETIAN;
  const parts: ShadePartBox[] = [];
  // head box (top at z = 0, hangs to -HEAD_BOX_H, projects +Y out of the facade)
  const hy0 = hidden ? -(C.headBoxD + C.mountStandoff) : 0;
  parts.push(boxAt('head', 'body', -w / 2, hy0, -C.headBoxH, w, C.headBoxD, C.headBoxH));
  // side guides (hang from the head box down to the bottom rail)
  const gdz = -(d - C.bottomRailH - C.headBoxH);
  parts.push(boxAt('guide-l', 'body', -w / 2, C.guideInset, -C.headBoxH, C.guideW, C.guideD, gdz));
  parts.push(boxAt('guide-r', 'body', w / 2 - C.guideW, C.guideInset, -C.headBoxH, C.guideW, C.guideD, gdz));
  // slats, from the head box down at a fixed pitch
  const count = venetianSlatCount(d);
  const slatLen = w - 2 * C.slatEndGap;
  const firstOffset = C.headBoxH + C.topGap + (C.slatDepth / 2) * Math.abs(Math.sin((tiltDeg * Math.PI) / 180));
  for (let i = 0; i < count; i += 1) {
    const zc = -(firstOffset + i * C.slatPitch);
    parts.push({
      key: `slat-${i}`,
      material: 'slat',
      center: [0, C.slatY, zc],
      size: [slatLen, C.slatDepth, C.slatThick],
      tiltXDeg: tiltDeg,
    });
  }
  // bottom rail (the hem bar)
  parts.push(
    boxAt('rail', 'railBody', -(w / 2 - C.slatEndGap), 8 * MM, -d, w - 2 * C.slatEndGap, C.bottomRailD, C.bottomRailH),
  );
  return parts;
}

/**
 * `shade_solids()` / `shutter_solids()` — head cassette, two zip/guide rails,
 * the fabric panel, bottom hem/seal bar. The cassette tucks back the same way
 * under `cassetteHidden`; the rails, fabric and hem never move with it.
 */
export function rollerParts(type: ShadeModelType, params: StyledShadeParams): ShadePartBox[] {
  const C = rollerConstants(type);
  const { widthM: w, dropM: d } = params;
  const hidden = params.cassetteHidden === true;
  const parts: ShadePartBox[] = [];
  const cy0 = hidden ? -(C.cassetteD + C.mountStandoff) : 0;
  parts.push(boxAt('cassette', 'body', -w / 2, cy0, -C.cassetteH, w, C.cassetteD, C.cassetteH));
  // side rails: from the cassette down to the hem
  const rdz = -(d - C.cassetteH - C.hemH);
  parts.push(boxAt('rail-l', 'railBody', -w / 2, C.railY, -C.cassetteH, C.railW, C.railD, rdz));
  parts.push(boxAt('rail-r', 'railBody', w / 2 - C.railW, C.railY, -C.cassetteH, C.railW, C.railD, rdz));
  // fabric panel (edges tucked behind the zip rails)
  const fw = w - 2 * C.fabricTuck;
  parts.push(
    boxAt('fabric', 'fabric', -fw / 2, C.fabricY, -C.cassetteH, fw, C.fabricThick, -(d - C.cassetteH)),
  );
  // bottom hem / seal bar
  parts.push(boxAt('hem', 'railBody', -(w / 2 - 6 * MM), C.railY, -d, w - 12 * MM, C.hemD, C.hemH));
  return parts;
}

/** Every part of one shade at one state, in the fixed build order. */
export function shadeParts(type: ShadeModelType, params: StyledShadeParams): ShadePartBox[] {
  return type === 'venetian-blind' ? venetianBlindParts(params) : rollerParts(type, params);
}
